import { api } from '../../api.js';
import type { PolicyDocumentRead, PolicyReportRow, PolicyReportView, PolicyRuleRow, PolicyRuleSaved } from '@agio/core';

export interface RuleInput {
  label_he: string;
  measure: string;
  /** Only for a combined line: the sleeves it adds together. */
  components: string[] | null;
  denominator: string;
  limit_min: number | null;
  limit_max: number | null;
  fund_ids: string[] | null;
  effective_from: string;
  effective_to: string | null;
  sort_order?: number;
}

/** A draft or published month as the admin sees it: the view plus what still blocks publishing. */
export type AdminPolicyReport = PolicyReportView & { blockers: string[] };

export interface BankCategory {
  bank_category: string;
  sleeve: string | null;
  positions: number;
  value: number;
  funds: string[];
  hedged: number;
  emerging: number;
  rated: number;
  with_duration: number;
}

const base = (clientId: string) => `/admin/clients/${encodeURIComponent(clientId)}/policy`;

export const policy = {
  rules: (clientId: string) => api.get<PolicyRuleRow[]>(`${base(clientId)}/rules`),
  createRule: (clientId: string, body: RuleInput) => api.post<PolicyRuleRow>(`${base(clientId)}/rules`, body),
  /** Fix a mistake in this version, in place. */
  updateRule: (clientId: string, ruleId: string, body: RuleInput) =>
    api.put<PolicyRuleSaved>(`${base(clientId)}/rules/${ruleId}`, body),
  /** The policy changed from body.effective_from: this version ends the month before, a new one starts. */
  changeRule: (clientId: string, ruleId: string, body: RuleInput) =>
    api.post<PolicyRuleSaved>(`${base(clientId)}/rules/${ruleId}/change`, body),
  /** The last month the line applies; null = no end. */
  endRule: (clientId: string, ruleId: string, lastMonth: string | null) =>
    api.post<PolicyRuleSaved>(`${base(clientId)}/rules/${ruleId}/end`, { last_month: lastMonth }),
  deleteRule: (clientId: string, ruleId: string) => api.del<{ ok: true }>(`${base(clientId)}/rules/${ruleId}`),
  /** PDF / Excel / Word → proposed rules. Saves nothing. */
  readDocument: (clientId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.upload<PolicyDocumentRead>(`${base(clientId)}/read-document`, form);
  },

  months: (clientId: string) => api.get<{ periods: string[]; reports: PolicyReportRow[] }>(`${base(clientId)}/reports`),
  report: (clientId: string, period: string) => api.get<AdminPolicyReport | null>(`${base(clientId)}/reports/${period}`),
  compute: (clientId: string, period: string) => api.post<AdminPolicyReport>(`${base(clientId)}/reports/${period}/compute`, {}),
  publish: (clientId: string, period: string) => api.post<AdminPolicyReport>(`${base(clientId)}/reports/${period}/publish`, {}),
  unpublish: (clientId: string, period: string) => api.post<{ ok: true }>(`${base(clientId)}/reports/${period}/unpublish`, {}),
  setManual: (resultId: string, manual_actual: number | null, manual_note: string | null) =>
    api.patch<AdminPolicyReport>(`/admin/policy/results/${resultId}/manual`, { manual_actual, manual_note }),
  acknowledge: (resultId: string, acknowledged: boolean) =>
    api.patch<{ ok: true }>(`/admin/policy/results/${resultId}/acknowledge`, { acknowledged }),

  bankCategories: (clientId: string) => api.get<BankCategory[]>(`/admin/clients/${encodeURIComponent(clientId)}/bank-categories`),
};
